import type React from "react";


interface BriefCoverProps {
  episode?: number | null;
  date?: string | null;
  title?: string;
  className?: string;
  priority?: boolean;
  compact?: boolean;
  /** Small square thumbnail (archive lists) */
  square?: boolean;
  /** Show the top meta row (brand + episode badge) on the square artwork */
  showMeta?: boolean;
  /** Rendered inside the artwork (e.g. the audio controls) */
  children?: React.ReactNode;
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Swiss-precision episode artwork: deep brand-red field, a light wash from the
 * top-right, one oversized ring cropped off the corner, a dark foot so the
 * lockup always reads, and a bottom-left "The Retail / Brief." lockup with a
 * monospaced episode badge.
 */
function Rings({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 200 200"
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
    >
      {[18, 32, 46, 60, 74, 88].map((r, i) => (
        <circle
          key={r}
          cx="100"
          cy="100"
          r={r}
          strokeWidth={i === 2 ? 1.4 : 0.6}
          opacity={i === 2 ? 0.55 : 0.28}
        />
      ))}
      <circle cx="100" cy="100" r="6" fill="currentColor" stroke="none" opacity="0.85" />
    </svg>
  );
}

/** Shared artwork furniture for the square cover. */
function CoverField() {
  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 100% 0%, rgba(255,255,255,0.28), transparent 62%)",
        }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-[18%] -top-[22%] aspect-square w-[62%] rounded-full border-[0.09em] border-white/15"
        style={{ borderWidth: "12%" }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/45 to-transparent"
      />
    </>
  );
}

export function BriefCover({
  episode,
  date,
  title,
  className = "",
  priority = false,
  compact = false,
  square = false,
  showMeta = false,
  children,
}: BriefCoverProps) {
  const dateLabel = formatDate(date);
  const alt = `The Retail Brief${episode ? ` episode #${episode}` : ""}${
    dateLabel ? `, ${dateLabel}` : ""
  }, RetailNews Asia audio briefing artwork`;

  /* ---------------------------------------------------------------- square */
  if (square) {
    return (
      <div
        className={`@container relative isolate aspect-square w-full overflow-hidden rounded-xl text-white ring-1 ring-black/10 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.7)] ${className}`}
        style={{ backgroundColor: "#b81615", backgroundImage: "var(--gradient-brand)" }}
        role="img"
        aria-label={alt}
      >
        <CoverField />
        <div className="absolute inset-0 flex flex-col justify-between p-[7cqw]">
          {showMeta ? (
          <div className="flex items-start justify-between gap-[4cqw]">
            <span className="font-sans text-[4.5cqw] font-bold uppercase leading-none tracking-[0.16em] text-white">
              Retail News Asia
            </span>
            {episode ? (
              <span className="rounded-[1.5cqw] border border-white/30 bg-white/15 px-[2.5cqw] py-[1cqw] font-mono text-[4.5cqw] leading-none text-white">
                #{episode}
              </span>
            ) : null}
          </div>
          ) : <span />}
          <div>
            <p className="font-sans text-[7cqw] font-medium uppercase leading-none tracking-[0.1em] text-white">
              The Retail
            </p>
            <p className="mt-[1.5cqw] font-serif text-[22cqw] font-bold leading-[0.85] tracking-tighter text-white">
              Brief.
            </p>
          </div>
        </div>
      </div>
    );
  }





  const hasChildren = Boolean(children);

  /* ------------------------------------------------------------------ hero */
  if (hasChildren) {
    return (
      <div
        className={`relative isolate w-full overflow-hidden rounded-2xl text-white ${className}`}
        style={{ backgroundImage: "var(--gradient-brand)" }}
      >
        <Rings className="pointer-events-none absolute -right-16 -top-20 h-80 w-80 text-white/25" />
        <div className="absolute inset-x-0 top-0 h-[3px] bg-white/40" aria-hidden="true" />

        <div className="relative flex flex-col gap-6 p-5 md:p-7">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 font-sans text-[9px] font-bold uppercase tracking-[0.3em] text-white/90 md:text-[10px]">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                Weekly audio briefing
              </p>
              <h2 className="mt-3 font-serif text-2xl font-bold leading-tight text-white md:text-4xl">
                {title || "The Retail Brief"}
              </h2>
              {dateLabel ? (
                <p className="mt-2 font-sans text-[10px] uppercase tracking-[0.2em] text-white/70">
                  {dateLabel} · 5 min listen
                </p>
              ) : null}
            </div>
            {episode ? (
              <span className="shrink-0 border border-white/60 px-2.5 py-1 font-sans text-[9px] font-bold uppercase tracking-[0.2em] text-white md:text-[10px]">
                Episode #{episode}
              </span>
            ) : null}
          </div>

          <div className="relative">{children}</div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- banner */
  return (
    <div
      className={`relative isolate w-full overflow-hidden rounded-2xl text-white ${className}`}
      style={{
        aspectRatio: compact ? "1200 / 400" : "1200 / 330",
        backgroundImage: "var(--gradient-brand)",
      }}
      role="img"
      aria-label={alt}
    >
      <Rings className="pointer-events-none absolute -right-10 top-1/2 h-64 w-64 -translate-y-1/2 text-white/25" />
      <div className="absolute inset-x-0 top-0 h-[3px] bg-white/40" aria-hidden="true" />

      <div
        className={`relative flex h-full flex-col justify-between gap-6 ${compact ? "p-4" : "p-5 md:p-6"}`}
      >
        <div className="flex items-start justify-between gap-4">
          <p className="font-sans text-[9px] font-bold uppercase tracking-[0.3em] text-white/90 md:text-[10px]">
            Weekly audio briefing
          </p>
          {episode ? (
            <span className="border border-white/60 px-2 py-1 font-sans text-[9px] font-bold uppercase tracking-[0.2em] text-white md:text-[10px]">
              Episode #{episode}
            </span>
          ) : null}
        </div>

        <div className="max-w-[62%]">
          <p
            className={`font-serif font-bold leading-tight text-white ${
              compact ? "text-base" : "text-xl md:text-2xl"
            }`}
          >
            {title || "The Retail Brief"}
          </p>
          {dateLabel ? (
            <p className="mt-1 font-sans text-[9px] uppercase tracking-wide text-white/70 md:text-[10px]">
              {dateLabel}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
